import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { MonthlyDataImporter, TemplateType } from '../components/import/MonthlyDataImporter';
import { MonthlyDataEditor } from '../components/edit/MonthlyDataEditor';
import { DatabaseBackup } from '../components/backup/DatabaseBackup';
import { BookingExcelImporter } from '../components/booking-import/BookingExcelImporter';
import { PropertyMappingVerifier } from '../components/booking-import/PropertyMappingVerifier';
import { exportMonthlyData } from '../services/monthlyDataService';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { supabase } from '../config/supabase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';

interface Property {
  id: string;
  name: string;
}

export const MonthlyDataPage = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('import');
  const [dataUpdated, setDataUpdated] = useState<boolean>(false);

  // Cargar propiedades al montar el componente
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const { data, error } = await supabase
          .from('properties')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setProperties(data || []);
        if (data && data.length > 0) {
          setSelectedPropertyId(data[0].id);
        }
      } catch (error: any) {
        console.error('Error al cargar propiedades:', error);
        toast.error('Error al cargar propiedades');
      }
    };

    fetchProperties();
  }, []);

  // Manejar la finalización de la importación
  const handleImportComplete = () => {
    setDataUpdated(prev => !prev); // Cambiar el estado para forzar la actualización de los datos
    toast.success('Datos importados correctamente');
  };
  
  // Manejar la actualización de datos
  const handleDataUpdated = () => {
    setDataUpdated(prev => !prev); // Cambiar el estado para forzar la actualización de los datos
  };

  // Manejar la exportación de datos mensuales
  const handleExportMonthlyData = async () => {
    setExportLoading(true);
    try {
      const monthlyData = await exportMonthlyData(selectedPropertyId || undefined);
      
      // Crear un libro de trabajo
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(monthlyData);
      XLSX.utils.book_append_sheet(wb, ws, 'Datos Mensuales');
      
      // Descargar el archivo
      XLSX.writeFile(wb, `datos_mensuales_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast.success('Datos mensuales exportados correctamente');
    } catch (error: any) {
      console.error('Error al exportar datos mensuales:', error);
      toast.error(error.message || 'Error al exportar datos mensuales');
    } finally {
      setExportLoading(false);
    }
  };

  // Generar años para el selector
  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 space-y-6">
        <h1 className="text-2xl font-bold">Gestión de Datos Mensuales</h1>
        
        <Tabs 
          defaultValue="import" 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="grid grid-cols-5 mb-6">
            <TabsTrigger value="import">Importar Datos</TabsTrigger>
            <TabsTrigger value="verify">Verificar Mapeo</TabsTrigger>
            <TabsTrigger value="booking">Libro Reservas</TabsTrigger>
            <TabsTrigger value="edit">Editar Datos</TabsTrigger>
            <TabsTrigger value="backup">Copias de Seguridad</TabsTrigger>
          </TabsList>
          
          <TabsContent value="import" className="space-y-6">
            <MonthlyDataImporter onImportComplete={handleImportComplete} />
            
            <Card>
              <div className="p-4">
                <h2 className="text-xl font-semibold mb-4">Exportar Datos</h2>
                <div className="flex items-center space-x-4">
                  <div className="flex-grow">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selecciona una propiedad para exportar sus datos mensuales:
                    </label>
                    <select
                      value={selectedPropertyId}
                      onChange={(e) => setSelectedPropertyId(e.target.value)}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                      <option value="">Todas las propiedades</option>
                      {properties.map((property) => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <Button
                    onClick={handleExportMonthlyData}
                    loading={exportLoading}
                    variant="outline"
                  >
                    Exportar a Excel
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>
          
          <TabsContent value="verify" className="space-y-6">
            <PropertyMappingVerifier />
            
            <Card>
              <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400">
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                  ⚠️ Importante: Verifica ANTES de importar
                </h3>
                <p className="text-sm text-yellow-800">
                  Esta verificación es <strong>obligatoria</strong> antes de usar el importador de Libro de Reservas.
                  Asegúrate de que todas las propiedades estén correctamente mapeadas y existan en Supabase.
                </p>
              </div>
            </Card>
          </TabsContent>
          
          <TabsContent value="booking" className="space-y-6">
            <BookingExcelImporter />
          </TabsContent>
          
          <TabsContent value="edit" className="space-y-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selecciona una propiedad para editar sus datos mensuales:
              </label>
              <select
                value={selectedPropertyId}
                onChange={(e) => setSelectedPropertyId(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="" disabled>Selecciona una propiedad</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedPropertyId && (
              <MonthlyDataEditor 
                propertyId={selectedPropertyId} 
                onDataUpdated={handleDataUpdated} 
                key={`${selectedPropertyId}-${dataUpdated}`} 
              />
            )}
          </TabsContent>
          
          <TabsContent value="backup" className="space-y-6">
            <DatabaseBackup />
          </TabsContent>
        </Tabs>

        {/* Instrucciones */}
        <Card>
          <div className="p-4">
            <h2 className="text-xl font-semibold mb-4">Instrucciones</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Tipos de Plantillas</h3>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm text-gray-600">
                  <li><strong>Datos Mensuales por Propiedad:</strong> Para subir datos de una propiedad específica para varios meses</li>
                  <li><strong>Múltiples Propiedades:</strong> Para subir datos de múltiples propiedades para un mes específico</li>
                  <li><strong>Histórico Completo:</strong> Para una carga masiva de datos históricos para múltiples propiedades y múltiples meses</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-medium">Proceso de Importación</h3>
                <ol className="list-decimal list-inside mt-2 space-y-1 text-sm text-gray-600">
                  <li>Selecciona el tipo de plantilla que deseas utilizar</li>
                  <li>Descarga la plantilla de Excel haciendo clic en "Descargar Plantilla"</li>
                  <li>Completa los datos en la plantilla siguiendo el formato indicado</li>
                  <li>Guarda el archivo y súbelo utilizando el selector de archivos</li>
                  <li>Haz clic en "Importar" para cargar los datos</li>
                  <li>Confirma los detalles de la importación</li>
                </ol>
              </div>
              
              <div>
                <h3 className="text-lg font-medium">Consideraciones Importantes</h3>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm text-gray-600">
                  <li>Los valores monetarios pueden incluir el símbolo € (ej: "628 €")</li>
                  <li>Los porcentajes pueden incluir el símbolo % (ej: "64%")</li>
                  <li>Para las propiedades, asegúrate de usar IDs existentes en el sistema</li>
                  <li>Los meses deben ser números del 1 al 12</li>
                  <li>Si encuentras errores, verifica el formato de tus datos</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};
